import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { isElementDomNode, invariant, ErrorConstant, isTextDomNode, extension, ExtensionTag, command, helper, PlainExtension } from '@remirror/core';
import { DOMSerializer } from '@remirror/pm/model';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import TurndownService from 'turndown';
import { marked } from 'marked';

/**
 * Converts the provide HTML to markdown.
 */

function htmlToMarkdown(html) {
  return turndownService.turndown(html);
}
/**
 * A tableRow is a heading row if:
 * - the parent is a THEAD
 * - or if its the first child of the TABLE or the first TBODY (possibly
 *   following a blank THEAD)
 * - and every cell is a TH
 */

function isHeadingRow(tableRow) {
  var parentNode = tableRow.parentNode;

  if (!isElementDomNode(parentNode)) {
    return false;
  }

  if (parentNode.nodeName === 'THEAD') {
    return true;
  }

  if (parentNode.nodeName !== 'TABLE' && !isFirstTbody(parentNode)) {
    return false;
  }

  var childNodes = [...tableRow.childNodes];
  return childNodes.every(n => n.nodeName === 'TH') && childNodes.some(n => !!n.textContent);
}
/**
 * Controller cells are generated by the React Tables extension, and provide Node Views for adding/removing columns and rows
 *
 * However they should not be included in markdown output.
 */


function isControllerHeadingCell(cell) {
  return isElementDomNode(cell) && cell.matches('th[data-controller-cell]');
}
/**
 * A tableRow is a controller heading row if:
 * - the parent is a THEAD
 * - or if its the first child of the TABLE or the first TBODY (possibly
 *   following a blank THEAD)
 * - and every cell is a controller cell
 */


function isControllerHeadingRow(tableRow) {
  var parentNode = tableRow.parentNode;

  if (!isElementDomNode(parentNode)) {
    return false;
  }

  if (parentNode.nodeName !== 'TABLE' && !isFirstTbody(parentNode)) {
    return false;
  }

  var childNodes = [...tableRow.childNodes];
  return childNodes.every(n => isControllerHeadingCell(n));
}
/**
 * Check whether this is the first `tbody` in the table.
 */


function isFirstTbody(element) {
  var _previousSibling$text;

  if (element.nodeName !== 'TBODY') {
    return false;
  }

  var previousSibling = element.previousSibling;

  if (!previousSibling) {
    return true;
  }

  return isElementDomNode(previousSibling) && previousSibling.nodeName === 'THEAD' && !((_previousSibling$text = previousSibling.textContent) !== null && _previousSibling$text !== void 0 && _previousSibling$text.trim());
}
/**
 * Markdown does not support nested tables, check if current table has a table ancestor node
 */


function isNestedTable(element) {
  var currentTable = element.closest('table');

  if (!currentTable) {
    return false;
  }

  var parentNode = currentTable.parentNode;

  if (!parentNode) {
    return true;
  }

  return !!parentNode.closest('table');
}
/**
 * Create a cell from the table.
 */


function cell(content, node) {
  var _node$parentNode$chil, _node$parentNode;

  var childNodes = [];

  var _iterator = _createForOfIteratorHelper((_node$parentNode$chil = (_node$parentNode = node.parentNode) === null || _node$parentNode === void 0 ? void 0 : _node$parentNode.childNodes) !== null && _node$parentNode$chil !== void 0 ? _node$parentNode$chil : []),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var n = _step.value;

      if (isControllerHeadingCell(n)) {
        continue;
      }

      childNodes.push(n);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var index = childNodes.indexOf(node);
  var prefix = index === 0 ? '| ' : ' ';
  return "".concat(prefix + content.trim(), " |");
}
/**
 * Create the turndown service which will be used to convert html to markdown.
 *
 * This supports html by default.
 */


var turndownService = new TurndownService({
  codeBlockStyle: 'fenced',
  headingStyle: 'atx'
}).addRule('taskListItems', {
  filter: node => {
    return node.nodeName === 'LI' && node.hasAttribute('data-task-list-item');
  },
  replacement: (content, node) => {
    var isChecked = node.hasAttribute('data-checked');
    return "- ".concat(isChecked ? '[x]' : '[ ]', " ").concat(content.trimStart());
  }
}).addRule('tableCell', {
  filter: ['th', 'td'],
  replacement: (content, node) => {
    if (isControllerHeadingCell(node)) {
      return '';
    }

    return cell(content, node);
  }
}).addRule('tableRow', {
  filter: 'tr',
  replacement: (content, node) => {
    var borderCells = '';
    var alignMap = {
      left: ':--',
      right: '--:',
      center: ':-:'
    }; // Get child nodes ignoring controller cells

    var childNodes = [...node.childNodes].filter(n => !isControllerHeadingCell(n));

    if (isHeadingRow(node)) {
      var _iterator2 = _createForOfIteratorHelper(childNodes),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _childNode$getAttribu;

          var childNode = _step2.value;

          if (!isElementDomNode(childNode)) {
            // This should never happen.
            continue;
          }

          var border = '---';
          var align = ((_childNode$getAttribu = childNode.getAttribute('align')) !== null && _childNode$getAttribu !== void 0 ? _childNode$getAttribu : '').toLowerCase();

          if (align) {
            border = alignMap[align] || border;
          }

          borderCells += cell(border, childNode);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    }

    return "\n".concat(content).concat(borderCells ? "\n".concat(borderCells) : '');
  }
}).addRule('table', {
  // Only convert tables with a heading row. Tables with no heading row are kept
  // using `keep` (see below).
  filter: node => {
    if (node.nodeName !== 'TABLE') {
      return false;
    }

    if (isNestedTable(node)) {
      return false;
    } // eslint-disable-next-line unicorn/prefer-array-find


    var rows = [...node.rows].filter(r => {
      // Remove controller rows
      return !isControllerHeadingRow(r);
    });
    return isHeadingRow(rows[0]);
  },
  replacement: content => {
    // Ensure there are no blank lines
    content = content.replace('\n\n', '\n');
    return "\n\n".concat(content, "\n\n");
  }
}).addRule('tableSection', {
  filter: ['thead', 'tbody', 'tfoot'],
  replacement: function replacement(content) {
    return content;
  }
}).keep(node => {
  return node.nodeName === 'TABLE' && !isHeadingRow(node.rows[0]);
}).keep(node => {
  return node.nodeName === 'TABLE' && isNestedTable(node);
}).addRule('strikethrough', {
  filter: ['del', 's', 'strike'],
  replacement: function replacement(content) {
    return "~".concat(content, "~");
  }
}) // Add improved code block support from html.
.addRule('fencedCodeBlock', {
  filter: (node, options) => {
    return !!(options.codeBlockStyle === 'fenced' && node.nodeName === 'PRE' && node.firstChild && node.firstChild.nodeName === 'CODE');
  },
  replacement: (_, node, options) => {
    var _node$firstChild, _node$firstChild$getA, _ref, _className$match$, _className$match;

    !isElementDomNode(node.firstChild) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.EXTENSION,
      message: "Invalid node `".concat((_node$firstChild = node.firstChild) === null || _node$firstChild === void 0 ? void 0 : _node$firstChild.nodeName, "` encountered for codeblock when converting html to markdown.")
    }) : invariant(false) : void 0;
    var className = (_node$firstChild$getA = node.firstChild.getAttribute('class')) !== null && _node$firstChild$getA !== void 0 ? _node$firstChild$getA : '';
    var language = (_ref = (_className$match$ = (_className$match = className.match(/(?:lang|language)-(\S+)/)) === null || _className$match === void 0 ? void 0 : _className$match[1]) !== null && _className$match$ !== void 0 ? _className$match$ : node.firstChild.getAttribute('data-code-block-language')) !== null && _ref !== void 0 ? _ref : '';
    return "\n\n".concat(options.fence).concat(language, "\n").concat(node.firstChild.textContent, "\n").concat(options.fence, "\n\n");
  }
});

/**
 * @module
 *
 * Provides the main method used to convert markdown to html.
 */
marked.use({
  renderer: {
    list(body, isOrdered, start) {
      if (isOrdered) {
        var startAttr = start !== 1 ? "start=\"".concat(start, "\"") : '';
        return "<ol ".concat(startAttr, ">\n").concat(body, "</ol>\n");
      }

      var taskListAttr = body.startsWith('<li data-task-list-item ') ? 'data-task-list' : '';
      return "<ul ".concat(taskListAttr, ">\n").concat(body, "</ul>\n");
    },

    listitem(text, isTask, isChecked) {
      if (!isTask) {
        return "<li>".concat(text, "</li>\n");
      }

      var checkedAttr = isChecked ? 'data-checked' : '';
      return "<li data-task-list-item ".concat(checkedAttr, ">").concat(text, "</li>\n");
    }

  }
});
/**
 * Converts the provided markdown to HTML.
 */

function markdownToHtml(markdown, sanitizer) {
  return marked(markdown, {
    gfm: true,
    smartLists: true,
    xhtml: true,
    sanitizer
  });
}

/**
 * The default sanitization method. Will throw if used in a non browser setting.
 */

function htmlSanitizer(html) {
  var doc = new DOMParser().parseFromString("<!DOCTYPE html><html><body>".concat(html, "</body>"), 'text/html');
  doc.normalize();
  sanitize(doc.body);
  return doc.body.innerHTML;
}
/**
 * Sanitize the provided node recursively.
 */

function sanitize(node) {
  // Do nothing for text nodes.
  if (isTextDomNode(node)) {
    return;
  } // Remove invalid nodes.


  if (!isElementDomNode(node) || /^(script|iframe|object|embed|svg)$/i.test(node.tagName)) {
    return node === null || node === void 0 ? void 0 : node.remove();
  } // Remove invalid attributes from the node.


  var _iterator = _createForOfIteratorHelper(node.attributes),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var name = _step.value.name;

      if (/^(class|id|name|href|src|alt|align|valign)$/i.test(name)) {
        continue;
      }

      node.attributes.removeNamedItem(name);
    } // Sanitize all children.

  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var _iterator2 = _createForOfIteratorHelper(node.childNodes),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var childNode = _step2.value;
      sanitize(childNode);
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
}

var _dec, _dec2, _dec3, _dec4, _class, _class2;
var MarkdownExtension = (_dec = extension({
  defaultOptions: {
    htmlToMarkdown,
    markdownToHtml,
    htmlSanitizer,
    activeNodes: [ExtensionTag.Code],
    copyAsMarkdown: false
  },
  staticKeys: ['htmlToMarkdown', 'markdownToHtml', 'htmlSanitizer']
}), _dec2 = command(), _dec3 = helper(), _dec4 = command(), _dec(_class = (_class2 = class MarkdownExtension extends PlainExtension {
  get name() {
    return 'markdown';
  }
  /**
   * Add the `markdown` string handler and `getMarkdown` state helper method.
   */


  onCreate() {
    this.store.setStringHandler('markdown', this.markdownToProsemirrorNode.bind(this));
  }

  createPlugin() {
    var clipboardTextSerializer = this.options.copyAsMarkdown ? slice => {
      var wrapper = document.createElement('div');
      var serializer = DOMSerializer.fromSchema(this.store.schema);
      wrapper.append(serializer.serializeFragment(slice.content)); // Here we take the sliced text and transform it into markdown.

      return this.options.htmlToMarkdown(wrapper.innerHTML);
    } : undefined;
    return {
      props: {
        clipboardTextSerializer
      }
    };
  }
  /**
   * Convert the markdown to a prosemirror node.
   */


  markdownToProsemirrorNode(options) {
    return this.store.stringHandlers.html(_objectSpread(_objectSpread({}, options), {}, {
      content: this.options.markdownToHtml(options.content, this.options.htmlSanitizer)
    }));
  }
  /**
   * Insert a markdown string as a ProseMirror Node.
   *
   * ```ts
   * commands.insertMarkdown('# Heading\nAnd content');
   * // => <h1 id="heading">Heading</h1><p>And content</p>
   * ```
   *
   * The content will be inlined by default if not a block node.
   *
   * ```ts
   * commands.insertMarkdown('**is bold.**')
   * // => <strong>is bold.</strong>
   * ```
   *
   * To always wrap the content in a block you can pass the following option.
   *
   * ```ts
   * commands.insertMarkdown('**is bold.**', { alwaysWrapInBlock: true });
   * // => <p><strong>is bold.</strong></p>
   * ```
   */


  insertMarkdown(markdown, options) {
    return props => {
      var state = props.state;
      var html = this.options.markdownToHtml(markdown, this.options.htmlSanitizer);
      html = !(options !== null && options !== void 0 && options.alwaysWrapInBlock) && html.startsWith('<p><') && html.endsWith('</p>\n') ? html.slice(3, -5) : "<div>".concat(html, "</div>");
      var fragment = this.store.stringHandlers.html({
        content: html,
        schema: state.schema,
        fragment: true
      });
      return this.store.commands.insertNode.original(fragment, _objectSpread(_objectSpread({}, options), {}, {
        replaceEmptyParentBlock: true
      }))(props);
    };
  }
  /**
   * Get the markdown content from the current document.
   *
   * @param state - the state provided to the `getMarkdown` method.
   */


  getMarkdown(state) {
    return this.options.htmlToMarkdown(this.store.helpers.getHTML(state));
  }
  /**
   * TODO add commands for plain text markdown
   * @notimplemented
   *
   * @internal
   */


  toggleBoldMarkdown() {
    return _ => {
      return false;
    };
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "insertMarkdown", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "insertMarkdown"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getMarkdown", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "getMarkdown"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleBoldMarkdown", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleBoldMarkdown"), _class2.prototype)), _class2)) || _class);

export { MarkdownExtension };
